/**
 * ChatListRow — linha estilo WhatsApp para lista de grupos de alerta.
 *
 * Props:
 *  - conversation: ConversationListItem (de @/api/chat)
 *  - onClick: handler de clique
 *
 * Visual:
 *  - Avatar com iniciais do client (cor estável por hash)
 *  - Título (negrito se unread > 0) + preview da última mensagem
 *  - Chip de ambiente (PRD/QA/DEV)
 *  - Tempo relativo + Badge de não lidos
 */
import { Avatar, Badge, Box, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import type { ConversationListItem } from '@/api/chat'

// Mesmos tokens do FeedItem.tsx (notificacoes)
const SEVERITY_COLORS: Record<string, string> = {
  debug: 'gray',
  info: 'blue',
  warning: 'yellow',
  critical: 'red',
  fatal: 'grape',
}

const ENV_COLORS: Record<string, { color: string; label: string }> = {
  PRD:  { color: 'red',    label: 'PRD' },
  PROD: { color: 'red',    label: 'PRD' },
  QA:   { color: 'yellow', label: 'QA'  },
  HML:  { color: 'yellow', label: 'HML' },
  DEV:  { color: 'gray',   label: 'DEV' },
}

/** Gera um índice estável 0-7 a partir da string, para escolher uma cor de avatar */
function stableColorIndex(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h % 8
}

const AVATAR_COLORS = [
  'teal', 'blue', 'violet', 'grape', 'indigo', 'cyan', 'green', 'orange',
]

function timeAgo(iso: string | null): string {
  if (!iso) return ''
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diffSec < 60)    return 'agora'
  if (diffSec < 3600)  return `${Math.floor(diffSec / 60)}min`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`
  return `${Math.floor(diffSec / 86400)}d`
}

function getInitials(client: string): string {
  return client
    .split(/[\s-_]+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

interface Props {
  conversation: ConversationListItem
  onClick: () => void
}

export function ChatListRow({ conversation, onClick }: Props) {
  const { client, environment, title, last_message_at, unread, last_message } = conversation
  const isUnread = unread > 0
  const avatarColor = AVATAR_COLORS[stableColorIndex(client)]
  const envMeta = ENV_COLORS[environment?.toUpperCase?.()] ?? { color: 'gray', label: environment }
  const severityColor = last_message?.severity
    ? (SEVERITY_COLORS[last_message.severity] ?? 'blue')
    : null

  return (
    <UnstyledButton
      onClick={onClick}
      style={{ width: '100%' }}
      aria-label={`Abrir conversa ${title}`}
    >
      <Group
        gap="sm"
        wrap="nowrap"
        px="md"
        py="sm"
        style={{
          borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
          background: isUnread ? 'var(--ai-bg-subtle)' : 'transparent',
          transition: 'background 0.15s',
        }}
      >
        {/* Avatar */}
        <Box style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar radius="md" color={avatarColor} size={44} src={conversation.avatar_url ?? undefined}>
            {getInitials(client)}
          </Avatar>
          {/* Badge de ambiente no canto inferior direito do avatar */}
          <Badge
            size="xs"
            color={envMeta.color}
            variant="filled"
            style={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              padding: '0 4px',
              fontSize: 9,
              lineHeight: '14px',
              pointerEvents: 'none',
            }}
          >
            {envMeta.label}
          </Badge>
        </Box>

        {/* Corpo: título + preview */}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text
            size="sm"
            fw={isUnread ? 700 : 500}
            lineClamp={1}
            style={{ color: isUnread ? 'var(--mantine-color-text)' : undefined }}
          >
            {title}
          </Text>

          <Group gap={4} wrap="nowrap" align="center">
            {severityColor && (
              <Box
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: `var(--mantine-color-${severityColor}-6, gray)`,
                }}
              />
            )}
            <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
              {last_message?.body ?? 'Nenhum alerta ainda'}
            </Text>
          </Group>
        </Stack>

        {/* Direita: tempo + contador de não lidos */}
        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
          <Text size="xs" c="dimmed">
            {timeAgo(last_message_at)}
          </Text>
          {isUnread && (
            <Badge
              color="inovareTeal"
              size="sm"
              circle
              aria-label={`${unread} mensagens não lidas`}
            >
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Stack>
      </Group>
    </UnstyledButton>
  )
}
